import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Tag, Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { useGooglePlaces, type Prediction } from "@/hooks/useGooglePlaces";

interface BookingWidgetProps {
  preselectedDestination?: string;
  compact?: boolean;
}

const BookingWidget = ({ preselectedDestination, compact }: BookingWidgetProps) => {
  const navigate = useNavigate();
  const [routeType, setRouteType] = useState<"one-way" | "round-trip">("one-way");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromPlaceId, setFromPlaceId] = useState("");
  const [toPlaceId, setToPlaceId] = useState("");
  const [passengers, setPassengers] = useState("2");
  const [promoCode, setPromoCode] = useState("");

  const fromPlaces = useGooglePlaces();
  const toPlaces = useGooglePlaces();

  const isAirportRoute = fromPlaceId.startsWith("db-airport-") || toPlaceId.startsWith("db-airport-");
  const maxPassengers = isAirportRoute ? 50 : 10;

  useEffect(() => {
    if (parseInt(passengers) > maxPassengers) {
      setPassengers(String(maxPassengers));
    }
  }, [maxPassengers]);

  const handleFromSelect = (prediction: Prediction) => {
    setFrom(prediction.description);
    setFromPlaceId(prediction.place_id);
    fromPlaces.setPredictions([]);
  };

  const handleToSelect = (prediction: Prediction) => {
    setTo(prediction.description);
    setToPlaceId(prediction.place_id);
    toPlaces.setPredictions([]);
  };

  const handleSearch = () => {
    navigate("/shuttle/vehicles", {
      state: {
        from, to, fromPlaceId, toPlaceId, routeType,
        passengers: parseInt(passengers),
        promoCode,
      },
    });
  };

  return (
    <div className="w-full">
      <div className="bg-card/95 backdrop-blur-sm rounded-2xl p-5 md:p-7 shadow-xl border border-border/40">
        {/* Title */}
        <h2 className="text-lg md:text-xl font-bold text-foreground mb-4 tracking-tight">
          Book your <span className="text-primary">transfer service</span>
        </h2>

        {/* Row 1: From / To */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wide">
              <MapPin className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-primary" />
              From
            </label>
            <PlaceAutocomplete
              placeholder="Airport, Hotel or Address"
              value={from}
              onChange={setFrom}
              onSelect={handleFromSelect}
              predictions={fromPlaces.predictions}
              loading={fromPlaces.loading}
              onSearch={fromPlaces.search}
              iconColor="hidden"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wide">
              <MapPin className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-accent" />
              To
            </label>
            <PlaceAutocomplete
              placeholder="Hotel, Address or Airport"
              value={to}
              onChange={setTo}
              onSelect={handleToSelect}
              predictions={toPlaces.predictions}
              loading={toPlaces.loading}
              onSearch={toPlaces.search}
              iconColor="hidden"
            />
          </div>
        </div>

        {/* Row 2: Route Type | Passengers | Promo | Search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          {/* Route Type Toggle */}
          <div className="shrink-0">
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wide">
              Trip
            </label>
            <div className="inline-flex bg-muted rounded-xl p-1 gap-0.5 h-[42px]">
              <button
                onClick={() => setRouteType("one-way")}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
                  routeType === "one-way"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                One Way
              </button>
              <button
                onClick={() => setRouteType("round-trip")}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
                  routeType === "round-trip"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Round Trip
              </button>
            </div>
          </div>

          {/* Passengers */}
          <div className="flex-1 min-w-[130px]">
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wide">
              <Users className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
              Passengers
            </label>
            <Select value={passengers} onValueChange={setPassengers}>
              <SelectTrigger className="h-[42px] bg-card border-border rounded-xl text-foreground text-sm font-medium hover:border-primary/40 transition-colors">
                <SelectValue placeholder="Passengers" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: maxPassengers }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {i + 1} {i === 0 ? "Passenger" : "Passengers"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Promo Code */}
          <div className="flex-1 min-w-[130px]">
            <label className="text-xs font-semibold text-muted-foreground mb-1.5 block uppercase tracking-wide">
              <Tag className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
              Promo Code
            </label>
            <input
              placeholder="Enter code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="flex h-[42px] w-full rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0 hover:border-primary/40 transition-colors"
            />
          </div>

          {/* Search Button */}
          <div className="shrink-0">
            <label className="text-xs font-semibold text-transparent mb-1.5 hidden sm:block uppercase tracking-wide">
              Search
            </label>
            <Button
              onClick={handleSearch}
              className="h-[42px] px-6 text-sm font-bold gap-2 rounded-xl w-full sm:w-auto"
            >
              <Search className="w-4 h-4" />
              Search
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { BookingWidget };
export default BookingWidget;
